import i18n from '@/i18n'
import { filterRulesMapped } from '@/tools/filterRules'
import { enterpriseService } from '@/services/enterprise.service'
import { authService } from '@/services/auth.service'
import { mapActions, mapGetters } from 'vuex'
import { DialogType, SnackbarStyle, ColvenTheme } from '@/constants/constants'
import EnterpriseDialogComponent from '@/components/enterprise/dialog/EnterpriseDialogComponent.vue'
import { dialogTypes } from '@/constants/dialogTypes'
import { DialogEnterprise, dialogUtil } from '@/tools/dialog'
import { clone } from 'ramda'
import Vue from 'vue'
import ScoringDialogComponent from '@/components/enterprise/scoring/ScoringDialogComponent.vue'
import RusConfigurationDialogComponent from '@/components/enterprise/ruses/RusConfigurationDialogComponent.vue'
import EventConfigurationDialogComponent from '@/components/enterprise/events/EventConfigurationDialogComponent.vue'
import EventTypeDialogComponent from '@/components/event-type/dialog/EventTypeDialogComponent.vue'
import StateTypeDialogComponent from '@/components/state-type/dialog/StateTypeDialogComponent.vue'
import EventDialogComponent from '@/components/event/EventDialogComponent.vue'
import HarvestDialogComponent from "@/components/enterprise/dialog/harvest/HarvestDialogComponent.vue"
import SectorDialogComponent from '@/components/sector/dialog/SectorDialogComponent.vue'
import { SCORING_SYSTEM } from '@colven/common-domain-lib/lib'
import { scoringService } from '@/services/scoring.service'


/**
 * Componente del ABM de empresas (tabla y dialog de creación/modificación)
 */
export default {
    name: 'EnterpriseABMComponent',
    components: {
        EnterpriseDialogComponent,
        ScoringDialogComponent,
        RusConfigurationDialogComponent,
        EventConfigurationDialogComponent,
        EventTypeDialogComponent,
        StateTypeDialogComponent,
        EventDialogComponent,
        SectorDialogComponent,
        HarvestDialogComponent,
    },
    data: () => ({
        // headers
        tableHeaders: [],
        // reglas de filtrado
        tableFilterRules: {},
        // datos de la tabla
        tableData: [],
        // key de los datos
        tableDataKey: '_id',
        // acciones (botones) de cada fila
        rowActions: [
            // modificar
            {
                id: 1,
                name: i18n.t('modify'),
                color: 'blue',
                icon: 'fa-pen'
            },
            // eliminar
            {
                id: 2,
                name: i18n.t('delete'),
                color: 'red',
                icon: 'fa-trash-alt'
            },
            // usar como plantilla
            {
                id: 3,
                name: i18n.t('template'),
                color: 'green',
                icon: 'fa-clone'
            }
        ],
        // botones que se agregan a la tabla
        tableComponentButtons: [],
        // tamaño de página para el paginador
        pageSize: undefined,
        // número de página para el paginador
        pageNumber: undefined,
        // total de objetos (empresas) de la base de datos
        pageTotal: 0,
        // dialog creación/modificación (componente y modelo)
        dialog: {
            ...dialogTypes.ENTERPRISE,
        },
        // para mostrar/ocultar el dialog de creación/modificación
        showEditDialog: false,
        // título del dialog
        editDialogTitle: i18n.t('enterprise.editDialog.titleNew'),
        // para activar/desactivar el loading de la tabla
        loadingTable: true,
        // para mostrar o no el diálogo de clonación
        showCloneDialog: false,
        disableSaveCloneBtn: false,
        cloneModel: {
            cloneId: null,
            name: null
        },
        validCloneForm: false,
        validationRules: {
            required: (value) => !!value || value === 0 || `${i18n.t('required')}`,
        },
        // mensajes de error para el nombre de empresa a clonar
        nameError: []
    }),
    computed: {
        /**
         * Breadcrumbs de los dialog
         */
        ...mapGetters('breadcrumbs', [
            'getBread'
        ]),
    },
    created() {
        /*
            Vuex watch del breadcrumb
        */
        this.unwatch = this.$store.watch(
            (state, getters) => getters['breadcrumbs/getBread'],
            (newBreads) => {
                const lastElemet = newBreads[newBreads.length - 1]
                if (!lastElemet) {
                    this.cancel()
                    return
                }
                this.dialog = dialogUtil.getDialog(lastElemet.key, DialogEnterprise)
            },
            { deep: true })
    },
    beforeDestroy() {
        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
        this.cleanBread()
    },
    mounted() {
        this.initialize()
        this.resetFields()
        this.openEnterprise()
    },
    methods: {
        /**
         * actions del breadcrumbs
         */
        ...mapActions('breadcrumbs', [
            'pushBread',
            'cleanBread'
        ]),
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        async initialize() {
            // Botones del TableComponent
            this.tableComponentButtons.push(
                {
                    id: 'edit-dialog',
                    tooltip: i18n.t('add'),
                    icon: 'add',
                    action: this.openCreateDialog.bind(this),
                    selectedData: false
                },
                {
                    id: 'masive-delete',
                    tooltip: i18n.t('multipleDelete'),
                    icon: 'delete_outline',
                    action: this.multipleDelete.bind(this),
                    selectedData: true
                })

            // total de empresas (para el paginador)
            this.pageTotal = await enterpriseService.getTotal()

            // Si hay query params en la URL, los setea para incluirlos en la llamada al backend
            if (this.$route.query.pageSize && this.$route.query.pageNumber) {
                this.pageSize = Number(this.$route.query.pageSize)
                this.pageNumber = Number(this.$route.query.pageNumber)

                // Valida que los qery params sean correctos y completos
                if ((this.pageSize && this.pageSize <= 0) || (this.pageNumber && this.pageNumber <= 0) || (this.pageNumber && !this.pageSize) || (!this.pageNumber && this.pageSize)) {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('error.invalidQueryParams'),
                        timeout: 6000,
                        style: SnackbarStyle.ERROR
                    })
                    this.pageSize = this.pageTotal
                    this.pageNumber = 1
                }
            } else {
                this.pageSize = this.pageTotal
                this.pageNumber = 1
            }

            // cargo la tabla con las cosas filtradas de acuerdo a los valores del selector
            this.getData()
        },
        /**
         * Refrescar la data
         */
        refreshTable() {
            this.getData(true)
        },
        /**
         * Para obtener los datos de la tabla
         * @param {*} update
         */
        async getData(update = false) {
            this.loadingTable = true
            if (update) {
                this.pageTotal = await enterpriseService.getTotal()
                this.pageSize = 50
                this.pageNumber = 1
            }
            const data = await enterpriseService.findAll(this.pageSize, this.pageNumber, !update)
            const lastTableHeaders = clone(this.tableHeaders)
            this.tableData.cleanAndUpdate(data.data)
            if (update) {
                const that = this
                Vue.nextTick(function () {
                    that.tableHeaders.cleanAndUpdate(lastTableHeaders)
                })
            } else {
                this.tableHeaders.cleanAndUpdate(data.headers)
                this.setTableFilterRules()
            }
            this.loadingTable = false
        },
        /**
         * Crea las reglas de filtrado
         */
        setTableFilterRules() {
            this.tableFilterRules = {}
            this.tableHeaders.forEach(header => {
                this.tableFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Acción lanzada cuando se hace click en el botón eliminar de una fila de la tabla
         * @param {*} id
         */
        async delete(id) {
            await enterpriseService.deleteOne(id)
            this.getData(true)
        },
        /**
         * Eliminación múltiple
         * @param {*} data
         */
        async multipleDelete(data) {
            this.openDialog({
                title: this.$t('delete'),
                text: this.$t('enterprise.multipleDeleteMessage'),
                type: DialogType.QUESTION,
                overlayOpacity: 0.5,
                actionButtons: true,
                yesAction: async () => {
                    const ids = data.map(e => e._id)
                    await enterpriseService.deleteMany(ids)
                    this.getData(true)
                },
                noAction: () => {
                }
            })
        },
        /**
         * Carga los datos para la edición
         * @param {*} id
         */
        async loadFields(id) {
            const data = await enterpriseService.findOne(id)
            scoringService.processScoringConfigInit(data.scoringConfiguration, data.timezone);
            if (data.blocked) {
                this.openDialog({
                    title: this.$t('enterprise.blockedEnterpriseDialog.title'),
                    text: this.$t('enterprise.blockedEnterpriseDialog.message', { name: data.name }),
                    type: DialogType.INFO,
                    overlayOpacity: 0.5,
                    actionButtons: false,
                    yesAction: () => {
                    },
                    noAction: () => {
                    }
                })
            } else if (data) {
                this.openEditDialog()
                const newModel = {
                    _id: data._id,
                    name: data.name,
                    address: data.address,
                    phone: data.phone,
                    cityId: data.cityId,
                    district: data.district,
                    provinceId: data.provinceId,
                    countryId: data.countryId,
                    cuit: data.cuit,
                    legalPersonType: data.legalPersonType,
                    email: data.email,
                    fax: data.fax,
                    customTimeSpan: data.customTimeSpan,
                    suspended: data.suspended,
                    additionalInformation1: data.additionalInformation1,
                    additionalInformation2: data.additionalInformation2,
                    themes: data.themes,
                    logo: data.logo,
                    loginLogo: data.loginLogo,
                    loginBackgroundImage: data.loginBackgroundImage,
                    scoringConfiguration: data.scoringConfiguration
                        ? data.scoringConfiguration
                        : {
                            scoringSystem: SCORING_SYSTEM.ADDITION,
                            additionMaxValue: null,
                            substractionInitialValue: null,
                            firstLevel: [],
                            secondLevel: {
                                timeWindow: null,
                                hardBraking: {
                                    sligth: {
                                        range: {
                                            min: null,
                                            max: null
                                        },
                                        weightedValue: null
                                    },
                                    medium: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    },
                                    serious: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    }
                                },
                                acceleration: {
                                    sligth: {
                                        range: {
                                            min: null,
                                            max: null
                                        },
                                        weightedValue: null
                                    },
                                    medium: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    },
                                    serious: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    }
                                },
                                corneringAcceleration: {
                                    sligth: {
                                        range: {
                                            min: null,
                                            max: null
                                        },
                                        weightedValue: null
                                    },
                                    medium: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    },
                                    serious: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    }
                                },
                                disconnection: {
                                    sligth: {
                                        range: {
                                            min: null,
                                            max: null
                                        },
                                        weightedValue: null
                                    },
                                    medium: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    },
                                    serious: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    }
                                }
                            },
                            thirdLevel: {
                                maxStopTime: null,
                                nightSchedule: {
                                    start: null,
                                    end: null
                                },
                                ralenti: {
                                    sligth: {
                                        range: {
                                            min: null,
                                            max: null
                                        },
                                        weightedValue: null
                                    },
                                    medium: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    },
                                    serious: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    }
                                },
                                continuousDriving: {
                                    sligth: {
                                        range: {
                                            min: null,
                                            max: null
                                        },
                                        weightedValue: null
                                    },
                                    medium: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    },
                                    serious: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    }
                                },
                                nightDriving: {
                                    sligth: {
                                        range: {
                                            min: null,
                                            max: null
                                        },
                                        weightedValue: null
                                    },
                                    medium: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    },
                                    serious: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    }
                                },
                                incompleteRest: {
                                    sligth: {
                                        range: {
                                            min: null,
                                            max: null
                                        },
                                        weightedValue: null
                                    },
                                    medium: {
                                        range: {
                                            min: null,
                                            max: null,
                                        },
                                        weightedValue: null
                                    },
                                    serious: {
                                        range: {
                                            min: 0,
                                            max: null,
                                        },
                                        weightedValue: null
                                    }
                                }
                            }
                        },
                    events: data.events,
                    menu: data.menu != null ? data.menu : [],
                    locales: data.locales != null ? data.locales : { es: {}, en: {}, pt: {} },
                    ruses: data.ruses,
                    sectors: data.sectors,
                    update: true,
                    timezoneId: data.timezoneId,
                    timezone: data.timezone,
                    harvestStartTime: {
                        start: data?.harvestStartTime?.start,
                        harvests: data?.harvestStartTime?.harvests,
                    }
                }
                const model = this.dialog.model
                Object.assign(model, newModel)
            } else {
                this.showSnackbar({
                    visible: true,
                    text: this.$t('enterprise.error.couldNotLoadEditDialog'),
                    timeout: 6000,
                    style: SnackbarStyle.ERROR
                })
            }
        },
        /**
         * Click en un botón de la fila
         * @param {*} button
         * @param {*} data
         */
        async rowButtonClicked(button, data) {
            switch (button.id) {
                case 1:
                    // TO DO: agregar el id como query param
                    this.editDialogTitle = i18n.t('enterprise.editDialog.titleUpdate')
                    this.loadFields(data._id)
                    break
                case 2:
                    this.openDialog({
                        title: this.$t('delete'),
                        text: this.$t('enterprise.deleteMessage', { name: data.name }),
                        type: DialogType.QUESTION,
                        overlayOpacity: 0.5,
                        actionButtons: true,
                        yesAction: () => {
                            this.delete(data._id)
                        },
                        noAction: () => {
                        }
                    })
                    break
                case 3:
                    if (this.validateCloneEnterprise(data)) {
                        this.showCloneDialog = true
                        this.cloneModel.cloneId = data._id
                    }
                    break
                default:
                    console.error('Botón no programado en el componente')
            }
        },

        validateCloneEnterprise(data) {
            // Ledesma ya no se se va a poder clonar
            if (data._id === '5e4fe3e1a2c651e4d8b95f0a') {
                this.showSnackbar({
                    visible: true,
                    text: this.$t('enterprise.errorCloneMessage'),
                    timeout: 6000,
                    style: SnackbarStyle.ERROR
                })
                return false
            }
            return true
        },
        /**
         * Si se hace click fuera del dialog, se ejecuta la misma función que el botçon cancelar
         */
        clickOutside() {
            this.cancel()
        },
        /**
         * Botón cancelar
         */
        cancel() {
            this.showEditDialog = false
            this.resetFields()
        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            this.dialog.model = {
                name: null, // razón social
                address: null, // dirección
                phone: null, // teléfono
                countryId: null, // id del país
                provinceId: null, // id de la provincia
                cityId: null, // id de la localidad
                district: null, // partido
                cuit: null, // cuit
                fax: null, // fax
                // TODO: TENER EN CUENTA TIMEZONE DE LA EMPRESA CUANDO SE IMPLEMENTE
                customTimeSpan: { start: 3 },
                timezoneId: 56,
                timezone: -3,
                email: null, // email
                suspended: false, // empresa suspendida
                additionalInformation1: null, // información adicional 1
                additionalInformation2: null, // información adicional 2
                themes: clone(ColvenTheme),
                logo: null,
                loginLogo: null,
                loginBackgroundImage: null,
                menu: [],
                locales: {
                    es: {},
                    en: {},
                    pt: {}
                },
                scoringConfiguration: {
                    scoringSystem: SCORING_SYSTEM.ADDITION,
                    additionMaxValue: null,
                    substractionInitialValue: null,
                    firstLevel: [],
                    secondLevel: {
                        timeWindow: null,
                        hardBraking: {
                            sligth: {
                                range: {
                                    min: null,
                                    max: null
                                },
                                weightedValue: null
                            },
                            medium: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            },
                            serious: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            }
                        },
                        acceleration: {
                            sligth: {
                                range: {
                                    min: null,
                                    max: null
                                },
                                weightedValue: null
                            },
                            medium: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            },
                            serious: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            }
                        },
                        corneringAcceleration: {
                            sligth: {
                                range: {
                                    min: null,
                                    max: null
                                },
                                weightedValue: null
                            },
                            medium: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            },
                            serious: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            }
                        },
                        disconnection: {
                            sligth: {
                                range: {
                                    min: null,
                                    max: null
                                },
                                weightedValue: null
                            },
                            medium: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            },
                            serious: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            }
                        }
                    },
                    thirdLevel: {
                        maxStopTime: null,
                        nightSchedule: {
                            start: null,
                            end: null
                        },
                        ralenti: {
                            sligth: {
                                range: {
                                    min: null,
                                    max: null
                                },
                                weightedValue: null
                            },
                            medium: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            },
                            serious: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            }
                        },
                        continuousDriving: {
                            sligth: {
                                range: {
                                    min: null,
                                    max: null
                                },
                                weightedValue: null
                            },
                            medium: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            },
                            serious: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            }
                        },
                        nightDriving: {
                            sligth: {
                                range: {
                                    min: null,
                                    max: null
                                },
                                weightedValue: null
                            },
                            medium: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            },
                            serious: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            }
                        },
                        incompleteRest: {
                            sligth: {
                                range: {
                                    min: null,
                                    max: null
                                },
                                weightedValue: null
                            },
                            medium: {
                                range: {
                                    min: null,
                                    max: null,
                                },
                                weightedValue: null
                            },
                            serious: {
                                range: {
                                    min: 0,
                                    max: null,
                                },
                                weightedValue: null
                            }
                        }
                    }
                },
                events: {},
                ruses: {},
                sectors: [],
                update: false
            }
        },
        /**
         * Para activar el diálogo de creación
         */
        openCreateDialog() {
            this.openEnterprise()
            this.resetFields()
            this.editDialogTitle = i18n.t('enterprise.editDialog.titleNew')
            this.showEditDialog = true
        },
        /**
         * Para activar el diálogo de modificación
         */
        openEditDialog() {
            this.openEnterprise()
            this.dialog.model.update = true
            this.showEditDialog = true
        },
        /**
         * Configura el breadcrumb del diálogo
         */
        openEnterprise() {
            const bread = {
                key: DialogEnterprise,
                disabled: true,
                text: this.$t('enterprise.name')
            }
            this.pushBread(bread)
        },
        /**
         * Para actualizar los valores del paginador
         * @param {*} pageSize
         * @param {*} pageNumber
         */
        externalPaginator(pageSize, pageNumber) {
            this.pageNumber = Number(pageNumber)
            this.pageSize = Number(pageSize)
        },
        /**
         * Validación de nombre duplicado
         */
        duplicatedEnterpriseNameValidation() {
            enterpriseService.validateName(this.cloneModel.name).then(value => {
                if (value) {
                    this.nameError.push(this.$t('enterprise.duplicate'))
                } else {
                    this.nameError = []
                }
            })
        },
        saveClone() {
            enterpriseService.clone(this.cloneModel).then(async () => {
                this.showSnackbar({
                    visible: true,
                    text: this.$t('enterprise.successCloneMessage'),
                    timeout: 6000,
                    style: SnackbarStyle.SUCCESS
                })
                this.cancelClone()
                await authService.refreshTokenPayload()
                this.getData(true)
            })
        },
        cancelClone() {
            this.showCloneDialog = false
            this.validCloneForm = false
            this.cloneModel = {
                cloneId: null,
                name: null
            }
        }
    }
}
